import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { hasCapability } from "@/lib/brokerAuth";
import { getAccount, type AccountDetail } from "@/lib/brokerAccounts";
import { messageFor } from "@/lib/errors";
import { usePreapprovalSubproductId } from "@/lib/usePreapprovalSubproduct";
import { AccountMessageForm } from "@/components/AccountMessageForm";
import { SuretyAccountInfo } from "@/components/SuretyAccountInfo";
import { SuretyProgramBlock } from "@/components/SuretyProgramBlock";
import { SuretyAccountTabs } from "@/components/SuretyAccountTabs";

// BRK-S138 — the Surety account PROFILE: one insured, as the bond side reads
// them.
//
// A SECOND VIEW of the same record, not a second record. `/accounts/{id}` is the
// Commercial-side card — the whole editable account, personnel and phones and
// two addresses. This page answers the questions a bond underwriter asks first:
// who is this insured, and what standing program do they hold (BRK-S139). Both
// read the SAME own-scoped `GET /api/broker/accounts/{id}`; PMS is the system of
// record and there is no surety-specific account (NN #9).
//
// Three blocks, in the order a broker reads them (BRK-S139): who the insured is,
// what standing program they hold, and then the tabs they browse.
//
// It lives under `/surety` so the platform is derived from the route
// (BRK-S090) — a broker who deep-links here stays in Surety, where the
// Commercial `/accounts` prefix is deliberately line-agnostic.
export function SuretyAccountPage() {
  const { id = "" } = useParams();
  const { identity, authedRequest } = useAuth();
  const canView = hasCapability(identity, "account:view");
  const canEdit = hasCapability(identity, "account:edit");

  const [detail, setDetail] = useState<AccountDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Which subproduct the "Get pre-approved" CTA links to (BRK-S160). `null`
  // while the catalog read is in flight, and `null` for good when the catalog
  // carries no standalone subproduct — both render today's CTA.
  const preapprovalSubproductId = usePreapprovalSubproductId();

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    (async () => {
      try {
        const fresh = await getAccount(authedRequest, id);
        if (!cancelled) setDetail(fresh);
      } catch (err) {
        // A 404 covers "no such account" AND "not your brokerage's" — the
        // own-scoping is server-side and deliberately indistinguishable, so
        // this renders PMS's own sentence rather than guessing which (NN #7).
        if (!cancelled) setLoadError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, id, canView]);

  return (
    <section className="flex w-full flex-col gap-6 py-8">
      <div className="flex flex-col gap-1">
        {/* The insured's name is the heading, and the account number sits under
            it — the two things a broker reads out to Signal. */}
        <h1 className="text-2xl font-bold tracking-tight">
          {detail ? detail.legalName : "Account"}
        </h1>
        {detail && (
          <p className="text-brk-dim">Account {detail.accountNumber}</p>
        )}
      </div>

      {!canView && (
        <p role="alert" className="text-sm text-brk-danger">
          You don’t have access to view this account.
        </p>
      )}

      {loadError && (
        <p role="alert" className="text-sm text-brk-danger">
          {loadError}
        </p>
      )}

      {canView && !detail && !loadError && (
        <p role="status" className="text-sm text-brk-dim">
          Loading account…
        </p>
      )}

      {detail && (
        <>
          <SuretyAccountInfo
            detail={detail}
            canEdit={canEdit}
            onUpdated={setDetail}
          />
          {/* The standing PROGRAM sits above the tabs: it is the fact a broker
              came here for, and the tabs are what they browse afterwards
              (BRK-S139). */}
          <SuretyProgramBlock
            accountId={detail.id}
            preapprovalSubproductId={preapprovalSubproductId}
            // An ABSENT block reads as `none` — the consumer-first window while
            // PMS-S315 is in flight (see `AccountRecord.preapproval`). That is
            // also the honest reading rather than a placeholder: a PMS that says
            // nothing about a program has not said the insured holds one, and
            // `none` is the state whose copy invites them to start one.
            preapproval={
              detail.preapproval ?? {
                status: "none",
                programType: null,
                terms: null,
                evaluatedAt: null,
                expiresAt: null,
                indicationAvailable: false,
              }
            }
          />
          <SuretyAccountTabs accountId={detail.id} />
          {/* "Message us about this account" (BRK-S159), now on BOTH account
              surfaces (BRK-S176). It was mounted only on the Commercial card,
              which meant a broker working a bond had to leave the bond view to
              ask a question about the very insured they were reading. The SAME
              component and the SAME route — a second composer would be a second
              set of validation rules for one Communication Log entry.

              LAST on the page, below the tabs: it is what a broker reaches for
              after reading the record, not before. */}
          {canEdit && <AccountMessageForm accountId={detail.id} />}
        </>
      )}

      <Link
        to="/surety/accounts"
        className="text-sm text-brk-accent hover:underline"
      >
        ← Back to accounts
      </Link>
    </section>
  );
}
